// CUSTOM: personalised-v2-filter-controller.js
// Unified filter controller for the v2 library page.
// Replaces the three separate mode JS files (mode-your-pack, mode-by-goal, mode-browse-all).
//
// Manages:
//   - Goal pill row (All + goal icons) — horizontal scroll with arrows
//   - "Filter by" menu (category/type filters, in-pack, recommended)
//   - Single product grid rendering with sort_order sort, in-pack at end
//   - Two-way reset: selecting a goal resets the filter menu to "All Products"
//     and selecting a filter menu item resets the goal pills to "All"
//
// Listens for: plv2:ready (from personalised-library-v2.js)
// Dispatches: plv2:view-changed (for sticky CTA / other listeners)
//
// Loaded with defer on page.personalised-library-v2.liquid.

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  var _productMap = null;   // Map<productId, ProductData>
  var _pageState = null;    // { hasReco, bundleItems, recoProductIds, quizGoals }
  var _activeGoal = 'all';  // 'all' or a goal handle
  var _activeFilter = 'all'; // 'all', 'in-pack', 'recommended', 'vitamins', etc.
  var _grid = null;         // [data-grid="main"] element
  var _pillsEl = null;      // .plv2-pills element
  var _goalCategories = []; // master goal list from JSON block
  var _menuOpen = false;

  // ---------------------------------------------------------------------------
  // Tag helper
  // ---------------------------------------------------------------------------

  function _hasTag(data, tagName) {
    var lower = tagName.toLowerCase();
    return Array.isArray(data.tags) && data.tags.some(function (t) {
      return t.toLowerCase() === lower;
    });
  }

  // ---------------------------------------------------------------------------
  // Sort helpers
  // ---------------------------------------------------------------------------

  function _sortBySortOrder(arr) {
    return arr.slice().sort(function (a, b) {
      return (a.sortOrder || 9999) - (b.sortOrder || 9999);
    });
  }

  // ---------------------------------------------------------------------------
  // Bundle / Reco helpers
  // ---------------------------------------------------------------------------

  function _getInPackIdSet() {
    var inPackIds = new Set();
    var bundle = window.personalised && window.personalised.bundle;
    if (!bundle || typeof bundle.getItems !== 'function') { return inPackIds; }
    bundle.getItems().forEach(function (item) {
      if (item && item.productId != null) {
        inPackIds.add(String(item.productId));
      }
    });
    return inPackIds;
  }

  function _getRecoIdSet() {
    var idSet = new Set();
    try {
      var keys = ['vitable_recommended_products', 'vitable_recommended_secondary'];
      keys.forEach(function (key) {
        var raw = localStorage.getItem(key);
        if (!raw) { return; }
        var arr = JSON.parse(raw);
        if (!Array.isArray(arr)) { return; }
        arr.forEach(function (item) {
          if (item && item.productId != null) {
            idSet.add(String(item.productId));
          }
        });
      });
    } catch (e) { /* silent */ }
    return idSet;
  }

  // ---------------------------------------------------------------------------
  // Filtering logic
  // ---------------------------------------------------------------------------

  /**
   * Get filtered + sorted product array based on the current active goal and filter.
   * Goal and filter are mutually exclusive (selecting one resets the other to "all").
   */
  function _getFilteredProducts() {
    if (!_productMap) { return []; }

    var allProducts = [];
    _productMap.forEach(function (data) {
      allProducts.push(data);
    });

    var inPackIds = _getInPackIdSet();
    var matched = [];

    // If a goal is active (not "all"), filter by goal
    if (_activeGoal !== 'all') {
      matched = allProducts.filter(function (d) {
        return d.goals && d.goals.some(function (g) {
          return g.handle === _activeGoal;
        });
      });
    }
    // If a filter menu item is active (not "all"), filter by type/category
    else if (_activeFilter !== 'all') {
      switch (_activeFilter) {
        case 'in-pack':
          matched = allProducts.filter(function (d) {
            return inPackIds.has(String(d.productId));
          });
          break;
        case 'recommended':
          var recoIds = _getRecoIdSet();
          matched = allProducts.filter(function (d) {
            return recoIds.has(String(d.productId));
          });
          break;
        case 'vitamins':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Vitamins'); });
          break;
        case 'minerals':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Minerals'); });
          break;
        case 'herbs':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Herbs'); });
          break;
        case 'probiotics':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Probiotics'); });
          break;
        case 'specialty':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Specialty'); });
          break;
        case 'powders':
          matched = allProducts.filter(function (d) { return _hasTag(d, 'Powders'); });
          break;
        default:
          matched = allProducts;
      }
    }
    // "All" — show everything
    else {
      matched = allProducts;
    }

    // Sort by sort_order, with in-pack products at the end
    var notInPack = matched.filter(function (d) { return !inPackIds.has(String(d.productId)); });
    var inPack    = matched.filter(function (d) { return  inPackIds.has(String(d.productId)); });
    return _sortBySortOrder(notInPack).concat(_sortBySortOrder(inPack));
  }

  // ---------------------------------------------------------------------------
  // Grid rendering
  // ---------------------------------------------------------------------------

  function _renderGrid() {
    if (!_grid) { return; }

    var products = _getFilteredProducts();
    var inPackIds = _getInPackIdSet();

    // Fade out
    _grid.classList.add('plv2-grid--fading');

    // Use rAF to let the fade apply before replacing content
    requestAnimationFrame(function () {
      _grid.classList.add('plv2-grid--rendering');
      _grid.innerHTML = '';

      if (products.length === 0) {
        var empty = document.createElement('p');
        empty.className = 'plv2-empty-state';
        empty.textContent = 'No products match this filter.';
        _grid.appendChild(empty);
      } else {
        products.forEach(function (data) {
          var template = document.querySelector('.plv2-card-template[data-product-id="' + data.productId + '"]');
          if (!template) { return; }
          var clone = template.cloneNode(true);
          clone.removeAttribute('hidden');
          clone.classList.remove('plv2-card-template');

          // Apply in-pack state (class + button/remove-icon via cardManager)
          var card = clone.querySelector('.plv2-card') || clone;
          var cardMgr = window.personalised && window.personalised.cardManager;
          if (inPackIds.has(String(data.productId))) {
            card.classList.add('plv2-card--in-pack');
            if (cardMgr && cardMgr.setCardStateOnElement) {
              cardMgr.setCardStateOnElement(card, 'in-pack', { origin: 'restore' }, String(data.productId), true);
            }
          } else {
            card.classList.remove('plv2-card--in-pack');
          }

          // Apply goal-specific description when filtering by a specific goal (all cards)
          if (_activeGoal !== 'all' && cardMgr && cardMgr.setCardStateOnElement) {
            if (!inPackIds.has(String(data.productId))) {
              // Non-in-pack: full sort-goal state (badge, icons, description)
              var goalDisplayName = '';
              for (var gi = 0; gi < _goalCategories.length; gi++) {
                if (_goalCategories[gi].handle === _activeGoal) {
                  goalDisplayName = _goalCategories[gi].display_name || '';
                  break;
                }
              }
              var goalDesc = '';
              if (data.goalDescriptions) {
                goalDesc = data.goalDescriptions[_activeGoal] || '';
                if (!goalDesc) {
                  var normHandle = _activeGoal.toLowerCase().replace(/[\s_]+/g, '-');
                  var keys = Object.keys(data.goalDescriptions);
                  for (var ki = 0; ki < keys.length; ki++) {
                    if (keys[ki].toLowerCase().replace(/[\s_]+/g, '-') === normHandle) {
                      goalDesc = data.goalDescriptions[keys[ki]];
                      break;
                    }
                  }
                }
              }
              cardMgr.setCardStateOnElement(card, 'sort-goal', {
                goalHandle: _activeGoal,
                goalDisplayName: goalDisplayName,
                goalDescription: goalDesc
              }, String(data.productId), true);
            } else {
              // In-pack: keep in-pack visual state but override description with goal text
              var goalDescInPack = '';
              if (data.goalDescriptions) {
                goalDescInPack = data.goalDescriptions[_activeGoal] || '';
                if (!goalDescInPack) {
                  var normHandleIp = _activeGoal.toLowerCase().replace(/[\s_]+/g, '-');
                  var keysIp = Object.keys(data.goalDescriptions);
                  for (var kj = 0; kj < keysIp.length; kj++) {
                    if (keysIp[kj].toLowerCase().replace(/[\s_]+/g, '-') === normHandleIp) {
                      goalDescInPack = data.goalDescriptions[keysIp[kj]];
                      break;
                    }
                  }
                }
              }
              if (goalDescInPack) {
                var descEl = card.querySelector('.plv2-card__description');
                if (descEl) { descEl.textContent = goalDescInPack; }
              }
            }
          }

          _grid.appendChild(clone);
        });
      }

      // Fade in — then cap icons once layout has settled
      requestAnimationFrame(function () {
        _grid.classList.remove('plv2-grid--rendering');
        _grid.classList.remove('plv2-grid--fading');

        // Cap icons to fit container width now that cards have layout dimensions
        var cardMgr = window.personalised && window.personalised.cardManager;
        if (cardMgr && cardMgr.capAllIcons) {
          cardMgr.capAllIcons(_grid);
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Goal pill row
  // ---------------------------------------------------------------------------

  function _parseGoalCategories() {
    var el = document.getElementById('v2-goal-categories');
    if (!el) { return []; }
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Canonical goal sort order — quiz goals first (ranked), then remainder A-Z.
   */
  var _CANONICAL_GOAL_ORDER = [
    'energy', 'sleep', 'stress', 'immunity', 'gut-health',
    'brain-health', 'heart-health', 'skin-health', 'hair-skin-nails',
    'eye-health', 'bone-joint', 'womens-health', 'mens-health',
    'prenatal', 'postnatal', 'weight-management', 'fitness-recovery',
    'detox', 'anti-ageing', 'general-health'
  ];

  function _canonicalSort(goals, quizGoals) {
    var quizSet = new Set((quizGoals || []).map(function (g) { return g.toLowerCase(); }));
    return goals.slice().sort(function (a, b) {
      var aIsQuiz = quizSet.has(a.handle);
      var bIsQuiz = quizSet.has(b.handle);
      if (aIsQuiz !== bIsQuiz) { return aIsQuiz ? -1 : 1; }
      var aIdx = _CANONICAL_GOAL_ORDER.indexOf(a.handle);
      var bIdx = _CANONICAL_GOAL_ORDER.indexOf(b.handle);
      if (aIdx === -1) { aIdx = 999; }
      if (bIdx === -1) { bIdx = 999; }
      return aIdx - bIdx;
    });
  }

  function _buildGoalPills() {
    if (!_pillsEl) { return; }
    _pillsEl.innerHTML = '';

    var quizGoals = (_pageState && _pageState.quizGoals) || [];
    var goals = _canonicalSort(_goalCategories, quizGoals);
    var quizGoalSet = new Set(quizGoals.map(function (g) { return g.toLowerCase(); }));

    // Exclude goals that have zero matching products
    if (_productMap && _productMap.size > 0) {
      var usedGoals = new Set();
      _productMap.forEach(function (d) {
        if (d.goals) d.goals.forEach(function (g) { usedGoals.add(g.handle); });
      });
      goals = goals.filter(function (g) { return usedGoals.has(g.handle); });
    }

    // "All" pill first
    var allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'plv2-pill';
    allBtn.setAttribute('role', 'radio');
    allBtn.setAttribute('aria-checked', _activeGoal === 'all' ? 'true' : 'false');
    allBtn.dataset.goal = 'all';
    var allLabel = document.createElement('span');
    allLabel.className = 'plv2-pill__label';
    allLabel.textContent = 'All';
    allBtn.appendChild(allLabel);
    allBtn.addEventListener('click', function () { _setGoal('all'); });
    _pillsEl.appendChild(allBtn);

    // Goal pills
    goals.forEach(function (goal) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'plv2-pill';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', _activeGoal === goal.handle ? 'true' : 'false');
      btn.dataset.goal = goal.handle;

      if (goal.icon_image) {
        var icon = document.createElement('img');
        icon.className = 'plv2-pill__icon';
        icon.src = goal.icon_image;
        icon.alt = '';
        icon.loading = 'lazy';
        btn.appendChild(icon);
      }

      var label = document.createElement('span');
      label.className = 'plv2-pill__label';
      label.textContent = goal.display_name;
      btn.appendChild(label);

      btn.addEventListener('click', function () { _setGoal(goal.handle); });
      _pillsEl.appendChild(btn);
    });

    // Init arrow buttons for horizontal scroll
    _initPillsArrows(_pillsEl);
  }

  function _setGoal(goalHandle) {
    if (_activeGoal === goalHandle) { return; }
    _activeGoal = goalHandle;

    // Reset filter menu to "all" (two-way reset)
    _activeFilter = 'all';
    _updateFilterMenuState();

    // Update pill aria states
    _pillsEl.querySelectorAll('.plv2-pill').forEach(function (p) {
      p.setAttribute('aria-checked', p.dataset.goal === _activeGoal ? 'true' : 'false');
    });

    _renderGrid();
    document.dispatchEvent(new CustomEvent('plv2:view-changed'));

    // Auto-scroll: if the clicked pill is at the visible edge,
    // nudge the list by one pill width so the next goal comes into view.
    var clickedPill = _pillsEl.querySelector('[data-goal="' + goalHandle + '"]');
    if (clickedPill) {
      var pillRect  = clickedPill.getBoundingClientRect();
      var trackRect = _pillsEl.getBoundingClientRect();
      var pillWidth = clickedPill.offsetWidth + 8; /* 8px = gap */
      if (pillRect.right >= trackRect.right - 4) {
        _pillsEl.scrollBy({ left: pillWidth, behavior: 'smooth' });
      } else if (pillRect.left <= trackRect.left + 4) {
        _pillsEl.scrollBy({ left: -pillWidth, behavior: 'smooth' });
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Pill scroll arrows
  // ---------------------------------------------------------------------------

  function _initPillsArrows(pillsEl) {
    // Wrap pills in a track div
    var track = document.createElement('div');
    track.className = 'plv2-pills-track';
    pillsEl.parentNode.insertBefore(track, pillsEl);
    track.appendChild(pillsEl);

    // Prev button
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'plv2-pills-arrow plv2-pills-arrow--prev';
    prevBtn.setAttribute('aria-label', 'Scroll goals left');
    prevBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 1.5L3 5l3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    prevBtn.setAttribute('hidden', '');
    track.appendChild(prevBtn);

    // Next button
    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'plv2-pills-arrow plv2-pills-arrow--next';
    nextBtn.setAttribute('aria-label', 'Scroll goals right');
    nextBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 1.5L7 5l-3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    track.appendChild(nextBtn);

    function _updateArrows() {
      var atStart = pillsEl.scrollLeft <= 0;
      var atEnd = pillsEl.scrollLeft + pillsEl.clientWidth >= pillsEl.scrollWidth - 1;
      var hasOverflow = pillsEl.scrollWidth > pillsEl.clientWidth;
      if (!hasOverflow || atStart) { prevBtn.setAttribute('hidden', ''); } else { prevBtn.removeAttribute('hidden'); }
      if (!hasOverflow || atEnd)   { nextBtn.setAttribute('hidden', ''); } else { nextBtn.removeAttribute('hidden'); }
    }

    prevBtn.addEventListener('click', function () {
      var pill = pillsEl.querySelector('.plv2-pill');
      var step = pill ? (pill.offsetWidth + 8) * 2 : 300;
      pillsEl.scrollBy({ left: -step, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      var pill = pillsEl.querySelector('.plv2-pill');
      var step = pill ? (pill.offsetWidth + 8) * 2 : 300;
      pillsEl.scrollBy({ left: step, behavior: 'smooth' });
    });
    pillsEl.addEventListener('scroll', _updateArrows);

    // Double rAF ensures layout is complete before measuring overflow
    requestAnimationFrame(function () {
      requestAnimationFrame(_updateArrows);
    });
  }

  // ---------------------------------------------------------------------------
  // Filter menu
  // ---------------------------------------------------------------------------

  function _initFilterMenu() {
    var btn = document.getElementById('plv2-filter-btn');
    var menu = document.getElementById('plv2-filter-menu');
    if (!btn || !menu) { return; }

    // Toggle menu
    btn.addEventListener('click', function () {
      _menuOpen = !_menuOpen;
      if (_menuOpen) {
        menu.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
        btn.querySelector('.plv2-filter-row__icon').textContent = '−';
      } else {
        _closeFilterMenu();
      }
    });

    // Close buttons (overlay + close X)
    menu.querySelectorAll('[data-filter-close]').forEach(function (el) {
      el.addEventListener('click', function () { _closeFilterMenu(); });
    });

    // Close on click outside menu and button
    document.addEventListener('click', function (e) {
      if (!_menuOpen) return;
      if (menu.contains(e.target) || btn.contains(e.target)) return;
      _closeFilterMenu();
    });

    // Filter item clicks
    menu.querySelectorAll('.plv2-filter-menu__item[data-filter]').forEach(function (item) {
      item.addEventListener('click', function () {
        var filterKey = item.dataset.filter;
        _setFilter(filterKey);
        _closeFilterMenu();
      });
    });

    // Show conditional items based on user state
    _updateConditionalFilters();
  }

  function _closeFilterMenu() {
    var btn = document.getElementById('plv2-filter-btn');
    var menu = document.getElementById('plv2-filter-menu');
    _menuOpen = false;
    if (menu) { menu.setAttribute('hidden', ''); }
    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.querySelector('.plv2-filter-row__icon').textContent = '+';
    }
  }

  function _setFilter(filterKey) {
    if (_activeFilter === filterKey && _activeGoal === 'all') { return; }
    _activeFilter = filterKey;

    // Reset goal pills to "all" (two-way reset)
    _activeGoal = 'all';
    if (_pillsEl) {
      _pillsEl.querySelectorAll('.plv2-pill').forEach(function (p) {
        p.setAttribute('aria-checked', p.dataset.goal === 'all' ? 'true' : 'false');
      });
    }

    _updateFilterMenuState();
    _renderGrid();
    document.dispatchEvent(new CustomEvent('plv2:view-changed'));
  }

  function _updateFilterMenuState() {
    var menu = document.getElementById('plv2-filter-menu');
    if (!menu) { return; }
    menu.querySelectorAll('.plv2-filter-menu__item[data-filter]').forEach(function (item) {
      var isActive = item.dataset.filter === _activeFilter;
      item.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });
  }

  function _updateConditionalFilters() {
    var menu = document.getElementById('plv2-filter-menu');
    if (!menu) { return; }

    // "In Your Pack" — show only when bundle is non-empty
    var inPackItem = menu.querySelector('[data-filter="in-pack"]');
    if (inPackItem) {
      var inPackIds = _getInPackIdSet();
      if (inPackIds.size > 0) {
        inPackItem.removeAttribute('hidden');
        if (inPackItem.parentElement) inPackItem.parentElement.removeAttribute('hidden');
      } else {
        inPackItem.setAttribute('hidden', '');
        if (inPackItem.parentElement) inPackItem.parentElement.setAttribute('hidden', '');
      }
    }

    // "Recommended for You" — show only when reco data exists
    var recoItem = menu.querySelector('[data-filter="recommended"]');
    if (recoItem) {
      var hasReco = _pageState && _pageState.hasReco;
      if (hasReco) {
        recoItem.removeAttribute('hidden');
        if (recoItem.parentElement) recoItem.parentElement.removeAttribute('hidden');
      } else {
        recoItem.setAttribute('hidden', '');
        if (recoItem.parentElement) recoItem.parentElement.setAttribute('hidden', '');
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Quiz CTA banner
  // ---------------------------------------------------------------------------

  function _initQuizCta() {
    var banner = document.getElementById('plv2-quiz-cta-banner');
    var introStrip = document.getElementById('plv2-intro-strip');

    // Show only for non-quiz users (no reco data)
    var hasReco = _pageState && _pageState.hasReco;
    if (!hasReco) {
      if (banner) { banner.removeAttribute('hidden'); }
      if (introStrip) { introStrip.removeAttribute('hidden'); }
    }
  }

  // ---------------------------------------------------------------------------
  // In-pack state change listener
  // ---------------------------------------------------------------------------

  function _initInPackListener() {
    // When a product is added/removed from the pack, update card states
    // and conditional filter visibility without re-sorting the grid
    document.addEventListener('plv2:in-pack-changed', function (e) {
      if (!e.detail) { return; }

      var productId = String(e.detail.productId);
      var isInPack = !!e.detail.isInPack;

      // Update card visual state in the current grid
      if (_grid) {
        var cards = _grid.querySelectorAll('[data-product-id="' + productId + '"] .plv2-card, .plv2-card[data-product-id="' + productId + '"]');
        cards.forEach(function (card) {
          if (isInPack) {
            card.classList.add('plv2-card--in-pack');
          } else {
            card.classList.remove('plv2-card--in-pack');
          }
        });
      }

      // Update conditional filter visibility
      _updateConditionalFilters();
    });

    // Re-apply goal description after card state changes from add/remove
    function _reapplyGoalDesc(productId) {
      if (_activeGoal === 'all' || !_grid || !_productMap) { return; }
      var data = _productMap.get(productId) || _productMap.get(String(productId));
      if (!data || !data.goalDescriptions) { return; }

      var goalDesc = data.goalDescriptions[_activeGoal] || '';
      if (!goalDesc) {
        var normHandle = _activeGoal.toLowerCase().replace(/[\s_]+/g, '-');
        var keys = Object.keys(data.goalDescriptions);
        for (var i = 0; i < keys.length; i++) {
          if (keys[i].toLowerCase().replace(/[\s_]+/g, '-') === normHandle) {
            goalDesc = data.goalDescriptions[keys[i]];
            break;
          }
        }
      }
      if (!goalDesc) { return; }

      var cards = _grid.querySelectorAll('[data-product-id="' + productId + '"] .plv2-card, .plv2-card[data-product-id="' + productId + '"]');
      cards.forEach(function (card) {
        var descEl = card.querySelector('.plv2-card__description');
        if (descEl) { descEl.textContent = goalDesc; }
      });
    }

    document.addEventListener('plv2:item-added', function (e) {
      if (e.detail && e.detail.productId) {
        _reapplyGoalDesc(String(e.detail.productId));
      }
      _updateConditionalFilters();
    });

    document.addEventListener('plv2:item-removed', function (e) {
      if (e.detail && e.detail.productId) {
        _reapplyGoalDesc(String(e.detail.productId));
      }
      _updateConditionalFilters();
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  function setGoal(goalHandle) {
    _setGoal(goalHandle || 'all');
  }

  function setFilter(filterKey) {
    _setFilter(filterKey || 'all');
  }

  function refresh() {
    _updateConditionalFilters();
    _renderGrid();
  }

  // ---------------------------------------------------------------------------
  // Initialisation — triggered by plv2:ready from personalised-library-v2.js
  // ---------------------------------------------------------------------------

  document.addEventListener('plv2:ready', function (e) {
    var lib = window.personalised && window.personalised.libraryV2;
    if (!lib) { return; }

    _productMap = lib.productMap;
    _pageState = lib.state;
    _grid = document.querySelector('[data-grid="main"]');
    _pillsEl = document.querySelector('.plv2-pills');
    _goalCategories = _parseGoalCategories();

    // Build the UI
    _buildGoalPills();
    _initFilterMenu();
    _initQuizCta();
    _initInPackListener();

    // Initial render — all products, sorted by sort_order
    _renderGrid();

    // Expose public API
    window.personalised.filterController = {
      setGoal: setGoal,
      setFilter: setFilter,
      refresh: refresh
    };

    console.debug('[FilterController] Initialised — goals:', _goalCategories.length, '| products:', _productMap.size);
  });

}());
